import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit2, Trash2, X, Building2, Mail, Phone, MapPin, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import TaxIdInput from '../../../components/admin/TaxIdInput';
import { TAX_ID_BY_KEY, defaultTaxIdForCountry } from '../../../config/taxIdTypes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [formData, setFormData] = useState({
    name: '', company_name: '', contact_person: '', email: '', phone: '', website: '',
    tax_id_type: 'TRN_UAE', trn: '', notes: '',
    address: { street: '', city: '', country: 'United Arab Emirates', postal_code: '' }
  });

  useEffect(() => { fetchCustomers(); }, [search]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await fetch(`${API_URL}/api/financial/customers?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load customers' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', company_name: '', contact_person: '', email: '', phone: '', website: '',
      tax_id_type: 'TRN_UAE', trn: '', notes: '',
      address: { street: '', city: '', country: 'United Arab Emirates', postal_code: '' }
    });
    setEditingCustomer(null);
  };

  const openCreate = () => { resetForm(); setShowModal(true); };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '', company_name: customer.company_name || '',
      email: customer.email || '', phone: customer.phone || '',
      website: customer.website || '',
      tax_id_type: customer.tax_id_type
        || (customer.trn ? defaultTaxIdForCountry(customer.address?.country) : 'TRN_UAE'),
      trn: customer.trn || '', notes: customer.notes || '',
      address: customer.address || { street: '', city: '', country: 'United Arab Emirates', postal_code: '' }
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingCustomer
        ? `${API_URL}/api/financial/customers/${editingCustomer.id}`
        : `${API_URL}/api/financial/customers`;
      const method = editingCustomer ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Failed');
      toast({ title: 'Success', description: `Customer ${editingCustomer ? 'updated' : 'created'} successfully` });
      setShowModal(false);
      fetchCustomers();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      const response = await fetch(`${API_URL}/api/financial/customers/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      toast({ title: 'Success', description: 'Customer deleted' });
      fetchCustomers();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete' });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="financial-customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Customers</h1>
          <p className="text-sm text-[#6C757D]">Finance &gt; Master Data &gt; Customers</p>
        </div>
        <Button onClick={openCreate} className="bg-[#FF6B35] hover:bg-[#E5562D]" data-testid="create-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> New Customer
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6C757D]" />
        <Input
          placeholder="Search customers..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10" data-testid="customer-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-[#6C757D]">Loading customers...</div>
      ) : customers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-[#6C757D]">No customers found. Create your first customer.</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {customers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-md transition-shadow" data-testid={`customer-card-${customer.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-[#FF6B35] bg-opacity-10 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#2B2D42]">{customer.name}</div>
                      {customer.company_name && customer.company_name !== customer.name && (
                        <div className="text-sm text-[#6C757D] flex items-center gap-1"><Building2 className="w-3 h-3" />{customer.company_name}</div>
                      )}
                      {customer.contact_person && (
                        <div className="text-sm text-[#6C757D] flex items-center gap-1" data-testid={`customer-contact-${customer.id}`}>
                          <Users className="w-3 h-3" /> {customer.contact_person}
                        </div>
                      )}
                      <div className="text-sm text-[#6C757D] flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {customer.trn && <Badge variant="outline" className="text-xs">TRN: {customer.trn}</Badge>}
                    {customer.website && (
                      <a
                        href={/^https?:\/\//i.test(customer.website) ? customer.website : `https://${customer.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm text-[#023E8A] hover:text-[#00B4D8] hover:underline inline-flex items-center gap-1 font-medium"
                        title="Visit website"
                        data-testid={`customer-website-link-${customer.id}`}
                      >
                        <Globe className="w-3 h-3" /> Visit website
                      </a>
                    )}
                    {customer.phone && <span className="text-sm text-[#6C757D] flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phone}</span>}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(customer)} data-testid={`edit-customer-${customer.id}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(customer.id)} data-testid={`delete-customer-${customer.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#2B2D42]">{editingCustomer ? 'Edit Customer' : 'New Customer'}</h3>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">
                    Customer / Company Name *
                  </label>
                  <Input required value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g. VC Horizonte 21"
                    data-testid="customer-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Trading / Brand Name</label>
                  <Input value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    placeholder="Optional — leave blank if same as above"
                    data-testid="customer-company-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">
                    Contact Person <span className="text-[#6C757D] font-normal">(primary contact at this customer)</span>
                  </label>
                  <Input value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    placeholder="e.g. Maria Silva"
                    data-testid="customer-contact-person-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">
                    Email <span className="text-[#6C757D] font-normal">(optional — add later)</span>
                  </label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Leave blank if unknown" data-testid="customer-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Phone</label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="customer-phone-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">
                    Tax / Registration Identifier
                  </label>
                  <TaxIdInput
                    typeValue={formData.tax_id_type}
                    valueValue={formData.trn}
                    onTypeChange={(t) => setFormData({ ...formData, tax_id_type: t })}
                    onValueChange={(v) => setFormData({ ...formData, trn: v })}
                    country={formData.address?.country}
                    testIdPrefix="customer-tax-id"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    Tip: choose the identifier matching the client's country
                    (UAE TRN, Angola NIF, Brasil CNPJ, USA EIN, UK VAT…).
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Website</label>
                  <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="https://www.example.com" data-testid="customer-website-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-1">Address</label>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Street" value={formData.address.street} onChange={(e) => setFormData({...formData, address: {...formData.address, street: e.target.value}})} />
                  <Input placeholder="City" value={formData.address.city} onChange={(e) => setFormData({...formData, address: {...formData.address, city: e.target.value}})} />
                  <Input placeholder="Country" value={formData.address.country} onChange={(e) => setFormData({...formData, address: {...formData.address, country: e.target.value}})} />
                  <Input placeholder="Postal Code" value={formData.address.postal_code} onChange={(e) => setFormData({...formData, address: {...formData.address, postal_code: e.target.value}})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-1">Notes</label>
                <textarea className="w-full px-3 py-2 border rounded-md text-sm" rows="2" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#FF6B35] hover:bg-[#E5562D]" data-testid="save-customer-btn">{editingCustomer ? 'Update' : 'Create'} Customer</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialCustomers;
